// Edita o cadastro completo de um cliente sem sair da tela atual
// (ex.: completar o cliente criado a partir de uma intimação, dentro do cadastro do processo).
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ClientForm } from '@/components/clients/ClientForm';

const LOGO = '/imgs/logos allana/letra_A_logo.png';

const toFormData = (client: any) => ({
  name: client.name || '',
  client_type: client.client_type || 'person',
  cpf_cnpj: client.cpf_cnpj || '',
  rg_ie: client.rg_ie || '',
  // Meio-dia local evita o dia anterior ao interpretar "AAAA-MM-DD"
  birth_date: client.birth_date ? new Date(`${client.birth_date}T12:00:00`) : undefined,
  nationality: client.nationality || 'Brasileira',
  marital_status: client.marital_status || '',
  profession: client.profession || '',
  email: client.email || '',
  phone: client.phone || '',
  mobile: client.mobile || '',
  alternative_phone: client.alternative_phone || '',
  address: client.address || '',
  address_number: client.address_number || '',
  complement: client.complement || '',
  district: client.district || '',
  city: client.city || '',
  state: client.state || '',
  zip_code: client.zip_code || '',
  country: client.country || 'Brasil',
  how_found_us: client.how_found_us || '',
  referral_source: client.referral_source || '',
  legal_areas: client.legal_areas || [],
  client_priority: client.client_priority || 'medium',
  notes: client.notes || '',
  special_requirements: client.special_requirements || '',
  confidentiality_level: client.confidentiality_level || 'normal',
  lgpd_consent: !!client.lgpd_consent,
  lgpd_consent_date: client.lgpd_consent_date ? new Date(client.lgpd_consent_date) : undefined,
  marketing_consent: client.marketing_consent || false,
  data_sharing_consent: client.data_sharing_consent || false,
});

interface Props {
  clientId: string | null;
  onOpenChange: (open: boolean) => void;
  onSaved?: (client: { id: string; name: string; email: string }) => void;
}

export const ClientQuickEditDialog = ({ clientId, onOpenChange, onSaved }: Props) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState<any>(null);

  useEffect(() => {
    if (!clientId) {
      setFormData(null);
      return;
    }
    supabase
      .from('clients')
      .select('*')
      .eq('id', clientId)
      .single()
      .then(({ data, error }) => {
        if (error || !data) {
          toast({ title: 'Cliente não encontrado', description: error?.message, variant: 'destructive' });
          onOpenChange(false);
          return;
        }
        setFormData(toFormData(data));
      });
  }, [clientId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!clientId || !formData) return;
    const dataToSave = {
      ...formData,
      birth_date: formData.birth_date?.toISOString().split('T')[0] || null,
      lgpd_consent_date: formData.lgpd_consent_date?.toISOString() || null,
      updated_at: new Date().toISOString(),
    };
    const { data, error } = await supabase.from('clients').update(dataToSave).eq('id', clientId).select('id, name, email');
    if (error) {
      const duplicate = error.code === '23505';
      toast({
        title: duplicate ? 'Dados já cadastrados' : 'Erro ao salvar o cliente',
        description: duplicate
          ? error.message?.includes('cpf_cnpj')
            ? 'Este CPF/CNPJ já pertence a outro cliente.'
            : error.message?.includes('email')
              ? 'Este email já pertence a outro cliente.'
              : 'Alguns dados já estão cadastrados em outro cliente.'
          : error.message,
        variant: 'destructive',
      });
      return;
    }
    const saved = data?.[0];
    toast({ title: 'Cliente atualizado', description: 'O cadastro do cliente foi completado.' });
    if (saved) onSaved?.({ id: saved.id, name: saved.name, email: saved.email || '' });
    onOpenChange(false);
  };

  return (
    <Dialog open={!!clientId} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto bg-white shadow-2xl border border-[#c4ab83]/30">
        <DialogHeader className="relative -m-6 mb-6 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
          <img src={LOGO} alt="" aria-hidden="true" className="pointer-events-none absolute -right-4 top-1/2 h-40 w-40 -translate-y-1/2 select-none object-contain opacity-[0.12]" />
          <div className="relative flex items-center gap-4 text-left">
            <img src={LOGO} alt="Allana Sthéfany" className="h-12 w-12 shrink-0 select-none object-contain opacity-80" />
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Clientes</p>
              <DialogTitle className="text-2xl font-semibold tracking-tight text-slate-900">Completar cadastro do cliente</DialogTitle>
              <DialogDescription className="mt-1 text-sm text-slate-500">
                Preencha os dados e salve. Você volta para o cadastro do processo.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        {formData ? (
          <ClientForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            onCancel={() => onOpenChange(false)}
            isEditing
          />
        ) : (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-[#c4ab83]" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
